import React, { useState, useEffect } from 'react';
import locationService from '../../services/locationService';
import './CascadingLocationSelector.css';

export const CascadingLocationSelector = ({
  countryId = 1, // Default to India (1)
  stateId = '',
  cityId = '',
  pinCode = '',
  address = '',
  countryName = 'India',
  stateName = '',
  cityName = '',
  onChange,
  layout = 'grid', // 'grid' | 'compact' | 'stacked'
  showAddress = true,
  showPinCode = true,
  showCity = true,
  showState = true,
  required = {},
  disabled = false,
  readOnly = false,
}) => {
  // Lists
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPinCodes] = useState([]);

  // Loading flags
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPinCodes, setLoadingPinCodes] = useState(false);
  const [resolvingPin, setResolvingPin] = useState(false);
  const [pinResolvedNotice, setPinResolvedNotice] = useState('');

  // 1. Fetch Countries on Mount
  useEffect(() => {
    let isMounted = true;
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await locationService.getCountries({ isActive: true });
        if (isMounted && res.success && res.data) {
          setCountries(res.data);
        }
      } catch (err) {
        console.error('Failed to load countries:', err);
      } finally {
        if (isMounted) setLoadingCountries(false);
      }
    };
    loadCountries();
    return () => { isMounted = false; };
  }, []);

  // 2. Fetch States when countryId changes
  useEffect(() => {
    let isMounted = true;
    if (!countryId) {
      setStates([]);
      return;
    }
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const res = await locationService.getStates({ countryId, isActive: true });
        if (isMounted && res.success && res.data) {
          setStates(res.data);
        }
      } catch (err) {
        console.error('Failed to load states:', err);
      } finally {
        if (isMounted) setLoadingStates(false);
      }
    };
    loadStates();
    return () => { isMounted = false; };
  }, [countryId]);

  // 3. Fetch Cities when stateId changes
  useEffect(() => {
    let isMounted = true;
    if (!stateId) {
      setCities([]);
      return;
    }
    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const res = await locationService.getCities({ stateId, isActive: true });
        if (isMounted && res.success && res.data) {
          setCities(res.data);
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
      } finally {
        if (isMounted) setLoadingCities(false);
      }
    };
    loadCities();
    return () => { isMounted = false; };
  }, [stateId]);

  // 4. Fetch PIN codes when cityId changes
  useEffect(() => {
    let isMounted = true;
    if (!cityId) {
      setPinCodes([]);
      return;
    }
    const loadPinCodes = async () => {
      setLoadingPinCodes(true);
      try {
        const res = await locationService.getPinCodes({ cityId, isActive: true });
        if (isMounted && res.success && res.data) {
          setPinCodes(res.data);
        }
      } catch (err) {
        console.error('Failed to load pincodes:', err);
      } finally {
        if (isMounted) setLoadingPinCodes(false);
      }
    };
    loadPinCodes();
    return () => { isMounted = false; };
  }, [cityId]);

  // Handle Country Selection
  const handleCountryChange = (e) => {
    const newCountryId = parseInt(e.target.value, 10) || '';
    const selected = countries.find((c) => c.countryId === newCountryId);
    onChange?.({
      countryId: newCountryId,
      countryName: selected ? selected.countryName : '',
      stateId: '',
      stateName: '',
      cityId: '',
      cityName: '',
      pinCode: '',
      address: '',
    });
    setPinResolvedNotice('');
  };

  // Handle State Selection
  const handleStateChange = (e) => {
    const newStateId = parseInt(e.target.value, 10) || '';
    const selected = states.find((s) => s.stateId === newStateId);
    onChange?.({
      countryId,
      countryName,
      stateId: newStateId,
      stateName: selected ? selected.stateName : '',
      cityId: '',
      cityName: '',
      pinCode: '',
      address,
    });
    setPinResolvedNotice('');
  };

  // Handle City Selection
  const handleCityChange = (e) => {
    const newCityId = parseInt(e.target.value, 10) || '';
    const selected = cities.find((c) => c.cityId === newCityId);
    onChange?.({
      countryId,
      countryName,
      stateId,
      stateName,
      cityId: newCityId,
      cityName: selected ? selected.cityName : '',
      pinCode: '',
      address,
    });
    setPinResolvedNotice('');
  };

  // Handle Smart PIN Code Resolution (6 digits)
  const handlePinCodeChange = async (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    onChange?.({
      countryId,
      countryName,
      stateId,
      stateName,
      cityId,
      cityName,
      pinCode: val,
      address,
    });

    if (val.length === 6) {
      setResolvingPin(true);
      try {
        const res = await locationService.resolvePinCode(val);
        if (res.success && res.data) {
          const resolved = res.data;
          setPinResolvedNotice(`Auto-resolved: ${resolved.cityName}, ${resolved.stateName}`);
          onChange?.({
            countryId: resolved.countryId,
            countryName: resolved.countryName,
            stateId: resolved.stateId,
            stateName: resolved.stateName,
            cityId: resolved.cityId,
            cityName: resolved.cityName,
            pinCode: val,
            address: address || resolved.areaName || '',
          });
        } else {
          setPinResolvedNotice('');
        }
      } catch (err) {
        setPinResolvedNotice('');
      } finally {
        setResolvingPin(false);
      }
    } else {
      setPinResolvedNotice('');
    }
  };

  // Handle Address Line Change
  const handleAddressChange = (e) => {
    onChange?.({
      countryId,
      countryName,
      stateId,
      stateName,
      cityId,
      cityName,
      pinCode,
      address: e.target.value,
    });
  };

  return (
    <div className={`cascading-location-root layout-${layout}`}>
      {/* Country Field */}
      <div className="loc-field-group col-country">
        <label className="loc-label">
          Country {required.country && <span className="loc-req">*</span>}
        </label>
        <div className={layout === 'unified-workbench' ? "field-control-with-icon" : "loc-select-wrap"}>
          {layout === 'unified-workbench' && <span className="field-prefix-icon-box">🌐</span>}
          <div className="loc-select-wrap" style={{ flex: 1, width: '100%' }}>
            <select
              className="loc-select"
              value={countryId || ''}
              onChange={handleCountryChange}
              disabled={disabled || readOnly || loadingCountries}
            >
              <option value="">-- Select Country --</option>
              {countries.map((c) => (
                <option key={c.countryId} value={c.countryId}>
                  {c.countryName} ({c.countryCode})
                </option>
              ))}
            </select>
            {loadingCountries && <span className="loc-spinner"></span>}
          </div>
        </div>
      </div>

      {/* State Field */}
      {showState && (
        <div className="loc-field-group col-state">
          <label className="loc-label">
            State / Province {required.state && <span className="loc-req">*</span>}
          </label>
          <div className={layout === 'unified-workbench' ? "field-control-with-icon" : "loc-select-wrap"}>
            {layout === 'unified-workbench' && <span className="field-prefix-icon-box">📖</span>}
            <div className="loc-select-wrap" style={{ flex: 1, width: '100%' }}>
              <select
                className="loc-select"
                value={stateId || ''}
                onChange={handleStateChange}
                disabled={disabled || readOnly || !countryId || loadingStates}
              >
                <option value="">
                  {!countryId ? 'Select country first' : '-- Select State --'}
                </option>
                {states.map((s) => (
                  <option key={s.stateId} value={s.stateId}>
                    {s.stateName} {s.stateCode ? `(${s.stateCode})` : ''}
                  </option>
                ))}
              </select>
              {loadingStates && <span className="loc-spinner"></span>}
            </div>
          </div>
        </div>
      )}

      {/* City Field */}
      {showCity && (
        <div className="loc-field-group col-city">
          <label className="loc-label">
            City {required.city && <span className="loc-req">*</span>}
          </label>
          <div className={layout === 'unified-workbench' ? "field-control-with-icon" : "loc-select-wrap"}>
            {layout === 'unified-workbench' && <span className="field-prefix-icon-box">📍</span>}
            <div className="loc-select-wrap" style={{ flex: 1, width: '100%' }}>
              <select
                className="loc-select"
                value={cityId || ''}
                onChange={handleCityChange}
                disabled={disabled || readOnly || !stateId || loadingCities}
              >
                <option value="">
                  {!stateId ? 'Select state first' : '-- Select City --'}
                </option>
                {cities.map((ci) => (
                  <option key={ci.cityId} value={ci.cityId}>
                    {ci.cityName}
                  </option>
                ))}
              </select>
              {loadingCities && <span className="loc-spinner"></span>}
            </div>
          </div>
        </div>
      )}

      {/* Address & PIN code placement: for unified-workbench, Address comes first to fill span 12 */}
      {layout === 'unified-workbench' ? (
        <>
          {/* Address Line Field */}
          {showAddress && (
            <div className="loc-field-group col-address">
              <label className="loc-label">
                Address (Street / Building / Locality) {required.address && <span className="loc-req">*</span>}
              </label>
              <div className="field-control-with-icon">
                <span className="field-prefix-icon-box">🏠</span>
                <input
                  type="text"
                  className="loc-input"
                  placeholder="Flat/House No, Building, Street, Area"
                  value={address || ''}
                  onChange={handleAddressChange}
                  disabled={disabled || readOnly}
                />
              </div>
            </div>
          )}

          {/* PIN Code Field */}
          {showPinCode && (
            <div className="loc-field-group col-pincode">
              <label className="loc-label">
                PIN Code {required.pinCode && <span className="loc-req">*</span>}
              </label>
              <div className="loc-input-wrap">
                <input
                  type="text"
                  className="loc-input"
                  maxLength={6}
                  placeholder="e.g. 122001"
                  value={pinCode || ''}
                  onChange={handlePinCodeChange}
                  disabled={disabled || readOnly}
                />
                {resolvingPin && <span className="loc-spinner"></span>}
              </div>
              {pinResolvedNotice && (
                <span className="loc-pin-notice">{pinResolvedNotice}</span>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* PIN Code Field */}
          {showPinCode && (
            <div className="loc-field-group col-pincode">
              <label className="loc-label">
                PIN Code {required.pinCode && <span className="loc-req">*</span>}
              </label>
              <div className="loc-input-wrap">
                <input
                  type="text"
                  className="loc-input"
                  maxLength={6}
                  placeholder="e.g. 122001"
                  value={pinCode || ''}
                  onChange={handlePinCodeChange}
                  disabled={disabled || readOnly}
                  list="pincode-options"
                />
                {(resolvingPin || loadingPinCodes) && <span className="loc-spinner"></span>}
                {pincodes && pincodes.length > 0 && (
                  <datalist id="pincode-options">
                    {pincodes.map((p) => (
                      <option key={p.pinCodeId || p.pinCode} value={p.pinCode}>
                        {p.areaName ? `${p.pinCode} - ${p.areaName}` : p.pinCode}
                      </option>
                    ))}
                  </datalist>
                )}
              </div>
              {pinResolvedNotice && (
                <span className="loc-pin-notice">{pinResolvedNotice}</span>
              )}
            </div>
          )}

          {/* Address Line Field */}
          {showAddress && (
            <div className="loc-field-group col-address">
              <label className="loc-label">
                Address (Street / Building / Locality) {required.address && <span className="loc-req">*</span>}
              </label>
              <input
                type="text"
                className="loc-input"
                placeholder="Flat/House No, Building, Street, Area"
                value={address || ''}
                onChange={handleAddressChange}
                disabled={disabled || readOnly}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CascadingLocationSelector;
